// A takeover (a tile in a TAKEOVER group) and its extra text and style,
// tile.presentation: { layout, eyebrow, firstLine, facts, actionLabel,
// moreLabel, moreLink, imageStyle, lightColor }. Limits as the spotlight
// service checks them (TilePresentationDTO).
export const TAKEOVER_TEXT_MAX = 30;
export const TAKEOVER_FACT_MAX = 24;
export const TAKEOVER_FACTS = 3;
export const TAKEOVER_LINK_MAX = 500;
// The image-and-text takeover's text (the tile's description)
export const TAKEOVER_BODY_MAX = 600;
// How long a takeover may wait after Home has loaded (group)
export const TAKEOVER_DELAY_MAX = 120;
export const TAKEOVER_TEXT_FIELDS = [
  'eyebrow',
  'firstLine',
  'actionLabel',
  'moreLabel',
];
// The light for clubs whose colour is black or very dark: mixed with white
// their colour is only grey, so a cool white instead
export const DARK_CLUB_LIGHT = '#E6EAF0';

const HEX6 = /^#[0-9a-f]{6}$/i;
// http(s) with a host that has a domain ending, as the service's IsUrl
const HTTP_URL = /^https?:\/\/[^\s/?#.]+(\.[^\s/?#.]+)+(:\d+)?([/?#]\S*)?$/i;

export const isLightColor = (value) => HEX6.test(value || '');
// The plain image-and-text takeover; anything else is the lit stage
export const isGenericTakeover = (presentation) =>
  presentation?.layout === 'GENERIC';
export const isHttpUrl = (value) => HTTP_URL.test(value || '');

const text = (value) => `${value ?? ''}`.trim();

// The form's values for a tile's presentation (always three fact inputs)
export const presentationToForm = (presentation) => ({
  layout: isGenericTakeover(presentation) ? 'GENERIC' : 'SPOTLIGHT',
  eyebrow: presentation?.eyebrow || '',
  firstLine: presentation?.firstLine || '',
  facts: Array.from(
    { length: TAKEOVER_FACTS },
    (_, index) => presentation?.facts?.[index] || ''
  ),
  actionLabel: presentation?.actionLabel || '',
  moreLabel: presentation?.moreLabel || '',
  moreLink: presentation?.moreLink || '',
  imageStyle: presentation?.imageStyle === 'CARD' ? 'CARD' : 'CUTOUT',
  lightColor: presentation?.lightColor || '',
});

/**
 * The presentation to save from the form's values: only what is filled in
 * (the spotlight layout and the cut-out image are the defaults, so left
 * out), or null when nothing is. The image-and-text layout leaves out what
 * only the spotlight shows, even if still in the form.
 */
export const presentationFromForm = (values) => {
  const generic = isGenericTakeover(values);
  const result = generic ? { layout: 'GENERIC' } : {};
  if (text(values?.eyebrow)) result.eyebrow = text(values.eyebrow);
  if (!generic && text(values?.firstLine)) {
    result.firstLine = text(values.firstLine);
  }
  const facts = (values?.facts || []).map(text).filter(Boolean);
  if (!generic && facts.length) result.facts = facts.slice(0, TAKEOVER_FACTS);
  ['actionLabel', 'moreLabel', 'moreLink'].forEach((key) => {
    if (text(values?.[key])) result[key] = text(values[key]);
  });
  if (!generic && values?.imageStyle === 'CARD') result.imageStyle = 'CARD';
  if (!generic && text(values?.lightColor)) {
    result.lightColor = text(values.lightColor);
  }
  return Object.keys(result).length ? result : null;
};

const channels = (hex) => {
  const num = parseInt(hex.slice(1, 7), 16);
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
};

// WCAG relative luminance (0 black – 1 white)
const luminance = (hex) => {
  const [r, g, b] = channels(hex).map((channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

// Near black of any hue, or a dark grey: what turns grey mixed with white
export const isVeryDark = (hex) => {
  if (!isLightColor(hex)) return false;
  const [r, g, b] = channels(hex);
  const chroma = (Math.max(r, g, b) - Math.min(r, g, b)) / 255;
  const lum = luminance(hex);
  return lum < 0.015 || (lum < 0.05 && chroma < 0.12);
};

// Readable text on a colour: dark on light ones, white on the rest
export const textOnColor = (hex) =>
  isLightColor(hex) && luminance(hex) > 0.4 ? '#121B2B' : '#FFFFFF';

/**
 * The colour of the takeover's light (as CSS): the tile's own, else the club
 * colour mixed 35% into white, or the cool white for a very dark club.
 */
export const takeoverLight = (lightColor, clubColor) => {
  if (isLightColor(lightColor)) return lightColor;
  if (!isLightColor(clubColor) || isVeryDark(clubColor)) {
    return DARK_CLUB_LIGHT;
  }
  return `color-mix(in srgb, ${clubColor} 35%, #FFFFFF)`;
};
